import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, FileText, Mail, CreditCard, LayoutTemplate, ChevronDown, ShieldAlert, Clock, Frown, Star, ArrowRight, Sparkles, Lock, Zap } from 'lucide-react';

// Direct-to-checkout permalink (skips the product page, adds the variant
// straight to cart and opens checkout). Format: /cart/{variant_id}:{quantity}
const SHOPIFY_PRODUCT_URL = 'https://checkout.digitalsolvex.shop/cart/44259514122328:1';

// Real product mockup images, hosted on Shopify Files.
// Falls back to the SVG DocMockup automatically if a URL ever 404s.
const DOC_IMAGES: Record<string, string> = {
  contract: 'https://cdn.shopify.com/s/files/1/0708/2174/0632/files/vaos-doc-contract.webp?v=1781849212',
  welcome: 'https://cdn.shopify.com/s/files/1/0708/2174/0632/files/vaos-doc-welcome.webp?v=1781849212',
  intake: 'https://cdn.shopify.com/s/files/1/0708/2174/0632/files/vaos-doc-intake.webp?v=1781849212',
  emails: 'https://cdn.shopify.com/s/files/1/0708/2174/0632/files/vaos-doc-emails.webp?v=1781849212',
  pricing: 'https://cdn.shopify.com/s/files/1/0708/2174/0632/files/vaos-doc-pricing.webp?v=1781849212'
};
const HERO_STACK_IMAGE = 'https://cdn.shopify.com/s/files/1/0708/2174/0632/files/vaos-hero-stack.webp?v=1781849212';

// ============================================================
// BRAND TOKENS — Digital Solvex
// Navy #0D1B2A · Teal #1D9E75 · Gold #C9A84C · Warm White #FAF9F6
// ============================================================

const FadeIn = ({
  children,
  delay = 0,
  className = '',
  y = 24
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
  y?: number;
}) => <motion.div initial={{
  opacity: 0,
  y
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-80px'
}} transition={{
  duration: 0.7,
  delay,
  ease: [0.21, 0.47, 0.32, 0.98]
}} className={className} data-id="element-1">
    {children}
  </motion.div>;
const Button = ({
  children,
  className = '',
  variant = 'primary'
}: {
  children: React.ReactNode;
  className?: string;
  variant?: 'primary' | 'gold';
}) => {
  const base = variant === 'gold' ? 'bg-[#C9A84C] hover:bg-[#D9BB66] text-[#0D1B2A] shadow-[0_0_40px_-8px_rgba(201,168,76,0.55)] hover:shadow-[0_0_60px_-10px_rgba(201,168,76,0.7)]' : 'bg-[#1D9E75] hover:bg-[#22B886] text-white shadow-[0_0_40px_-8px_rgba(29,158,117,0.55)] hover:shadow-[0_0_60px_-10px_rgba(29,158,117,0.7)]';
  return <a href={SHOPIFY_PRODUCT_URL} className={`inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-xl transition-all duration-300 hover:-translate-y-1 ${base} ${className}`} data-id="element-2">
      {children}
    </a>;
};

// Ambient animated background orbs — replaces the static blur blob
const AmbientField = () => <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-3">
    <motion.div animate={{
    x: [0, 40, 0],
    y: [0, 30, 0]
  }} transition={{
    duration: 18,
    repeat: Infinity,
    ease: 'easeInOut'
  }} className="absolute -top-32 left-[10%] w-[600px] h-[600px] bg-[#1D9E75]/20 blur-[140px] rounded-full" data-id="element-4" />
    <motion.div animate={{
    x: [0, -30, 0],
    y: [0, 40, 0]
  }} transition={{
    duration: 22,
    repeat: Infinity,
    ease: 'easeInOut'
  }} className="absolute top-20 right-[5%] w-[500px] h-[500px] bg-[#C9A84C]/15 blur-[130px] rounded-full" data-id="element-5" />
    {/* faint dot grid for texture */}
    <div className="absolute inset-0 opacity-[0.04]" style={{
    backgroundImage: 'radial-gradient(circle, #FAF9F6 1px, transparent 1px)',
    backgroundSize: '32px 32px'
  }} data-id="element-6" />
  </div>;

// ---- SVG Document Mockups (signature element) ----
// Each mockup is a hand-built vector "document" using brand colors,
// standing in for the real Canva/Docs exports until final assets are ready.

const DocMockup = ({
  kind,
  className = ''
}: {
  kind: 'contract' | 'welcome' | 'intake' | 'emails' | 'pricing';
  className?: string;
}) => {
  const [imgFailed, setImgFailed] = useState(false);
  const imgSrc = DOC_IMAGES[kind];
  if (imgSrc && !imgFailed) {
    return <img src={imgSrc} alt={`${kind} document preview`} loading="lazy" onError={() => setImgFailed(true)} className={`object-cover ${className}`} data-id="element-7" />;
  }
  const header = (label: string) => <>
      <rect x="0" y="0" width="240" height="64" rx="10" fill={`url(#hdr-${kind})`} data-id="element-8" />
      <rect x="0" y="44" width="240" height="20" fill={`url(#hdr-${kind})`} data-id="element-9" />
      <circle cx="24" cy="28" r="8" fill="#C9A84C" data-id="element-10" />
      <text x="40" y="32" fontFamily="Inter, sans-serif" fontSize="11" fontWeight="700" fill="#FAF9F6" letterSpacing="0.5" data-id="element-11">
        {label}
      </text>
    </>;
  return <svg viewBox="0 0 240 320" className={className} data-id="element-12">
      <defs data-id="element-13">
        <linearGradient id={`hdr-${kind}`} x1="0" y1="0" x2="1" y2="1" data-id="element-14">
          <stop offset="0%" stopColor="#0D1B2A" data-id="element-15" />
          <stop offset="100%" stopColor="#16293f" data-id="element-16" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="240" height="320" rx="10" fill="#FAF9F6" data-id="element-17" />
      <rect x="0" y="0" width="240" height="320" rx="10" fill="none" stroke="#1D1D1B" strokeOpacity="0.06" data-id="element-18" />

      {kind === 'contract' && <>
          {header('SERVICE CONTRACT')}
          <rect x="20" y="84" width="130" height="6" rx="3" fill="#0D1B2A" opacity="0.7" data-id="element-19" />
          <rect x="20" y="100" width="200" height="3" rx="1.5" fill="#0D1B2A" opacity="0.15" data-id="element-20" />
          <rect x="20" y="110" width="200" height="3" rx="1.5" fill="#0D1B2A" opacity="0.15" data-id="element-21" />
          <rect x="20" y="120" width="160" height="3" rx="1.5" fill="#0D1B2A" opacity="0.15" data-id="element-22" />
          <rect x="20" y="140" width="90" height="5" rx="2.5" fill="#1D9E75" opacity="0.8" data-id="element-23" />
          <rect x="20" y="154" width="200" height="3" rx="1.5" fill="#0D1B2A" opacity="0.15" data-id="element-24" />
          <rect x="20" y="164" width="180" height="3" rx="1.5" fill="#0D1B2A" opacity="0.15" data-id="element-25" />
          <rect x="20" y="184" width="90" height="5" rx="2.5" fill="#1D9E75" opacity="0.8" data-id="element-26" />
          <rect x="20" y="198" width="200" height="3" rx="1.5" fill="#0D1B2A" opacity="0.15" data-id="element-27" />
          <rect x="20" y="208" width="140" height="3" rx="1.5" fill="#0D1B2A" opacity="0.15" data-id="element-28" />
          <rect x="20" y="250" width="90" height="40" rx="6" fill="none" stroke="#C9A84C" strokeWidth="1.5" strokeDasharray="3 3" data-id="element-29" />
          <path d="M30 272 Q40 260 50 272 T70 272 T90 268" stroke="#C9A84C" strokeWidth="2" fill="none" strokeLinecap="round" data-id="element-30" />
          <rect x="130" y="250" width="90" height="40" rx="6" fill="none" stroke="#0D1B2A" strokeOpacity="0.2" strokeWidth="1.5" strokeDasharray="3 3" data-id="element-31" />
        </>}

      {kind === 'welcome' && <>
          {header('WELCOME PACKET')}
          <rect x="0" y="64" width="240" height="80" fill="#1D9E75" opacity="0.12" data-id="element-32" />
          <circle cx="120" cy="104" r="22" fill="#1D9E75" opacity="0.25" data-id="element-33" />
          <rect x="20" y="160" width="100" height="6" rx="3" fill="#0D1B2A" opacity="0.7" data-id="element-34" />
          <rect x="20" y="176" width="200" height="3" rx="1.5" fill="#0D1B2A" opacity="0.15" data-id="element-35" />
          <rect x="20" y="186" width="190" height="3" rx="1.5" fill="#0D1B2A" opacity="0.15" data-id="element-36" />
          <rect x="20" y="196" width="170" height="3" rx="1.5" fill="#0D1B2A" opacity="0.15" data-id="element-37" />
          <rect x="20" y="220" width="92" height="60" rx="6" fill="#0D1B2A" opacity="0.04" data-id="element-38" />
          <rect x="128" y="220" width="92" height="60" rx="6" fill="#C9A84C" opacity="0.12" data-id="element-39" />
          <rect x="34" y="234" width="64" height="4" rx="2" fill="#1D9E75" data-id="element-40" />
          <rect x="34" y="244" width="50" height="3" rx="1.5" fill="#0D1B2A" opacity="0.2" data-id="element-41" />
          <rect x="142" y="234" width="64" height="4" rx="2" fill="#C9A84C" data-id="element-42" />
          <rect x="142" y="244" width="50" height="3" rx="1.5" fill="#0D1B2A" opacity="0.2" data-id="element-43" />
        </>}

      {kind === 'intake' && <>
          {header('CLIENT INTAKE FORM')}
          {[0, 1, 2, 3].map(i => <g key={i} transform={`translate(20, ${88 + i * 46})`} data-id="element-44">
              <rect width="120" height="6" rx="3" fill="#0D1B2A" opacity="0.6" data-id="element-45" />
              <rect y="16" width="200" height="22" rx="5" fill="#0D1B2A" opacity="0.05" data-id="element-46" />
              <rect y="16" width="200" height="22" rx="5" fill="none" stroke="#1D9E75" strokeOpacity="0.3" data-id="element-47" />
            </g>)}
          <circle cx="210" cy="278" r="14" fill="#1D9E75" data-id="element-48" />
          <path d="M204 278 l4 4 l8 -9" stroke="#FAF9F6" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" data-id="element-49" />
        </>}

      {kind === 'emails' && <>
          {header('EMAIL SCRIPTS PACK')}
          {[0, 1, 2, 3, 4].map(i => <g key={i} transform={`translate(20, ${84 + i * 38})`} data-id="element-50">
              <rect width="200" height="30" rx="6" fill={i === 1 ? '#1D9E75' : '#0D1B2A'} opacity={i === 1 ? 0.1 : 0.04} data-id="element-51" />
              <circle cx="14" cy="15" r="6" fill="#C9A84C" opacity="0.8" data-id="element-52" />
              <rect x="28" y="9" width="100" height="4" rx="2" fill="#0D1B2A" opacity="0.55" data-id="element-53" />
              <rect x="28" y="18" width="150" height="3" rx="1.5" fill="#0D1B2A" opacity="0.2" data-id="element-54" />
            </g>)}
        </>}

      {kind === 'pricing' && <>
          {header('PRICING GUIDE')}
          <rect x="20" y="84" width="200" height="3" rx="1.5" fill="#0D1B2A" opacity="0.15" data-id="element-55" />
          <rect x="20" y="94" width="140" height="3" rx="1.5" fill="#0D1B2A" opacity="0.15" data-id="element-56" />
          {[0, 1, 2].map(i => <g key={i} transform={`translate(${20 + i * 68}, 116)`} data-id="element-57">
              <rect width="60" height="150" rx="8" fill={i === 1 ? '#0D1B2A' : '#FAF9F6'} stroke={i === 1 ? '#C9A84C' : '#0D1B2A'} strokeOpacity={i === 1 ? 1 : 0.12} strokeWidth={i === 1 ? 1.5 : 1} data-id="element-58" />
              <rect x="10" y="14" width="40" height="4" rx="2" fill={i === 1 ? '#C9A84C' : '#0D1B2A'} opacity={i === 1 ? 1 : 0.5} data-id="element-59" />
              <rect x="10" y="28" width="28" height="8" rx="2" fill={i === 1 ? '#FAF9F6' : '#1D9E75'} opacity={i === 1 ? 1 : 0.8} data-id="element-60" />
              {[0, 1, 2, 3].map(j => <rect key={j} x="10" y={50 + j * 14} width="36" height="2.5" rx="1.25" fill={i === 1 ? '#FAF9F6' : '#0D1B2A'} opacity={i === 1 ? 0.5 : 0.15} data-id="element-61" />)}
              <rect x="10" y="124" width="40" height="14" rx="4" fill={i === 1 ? '#C9A84C' : '#0D1B2A'} opacity={i === 1 ? 1 : 0.08} data-id="element-62" />
            </g>)}
        </>}
    </svg>;
};
export function LandingPage() {
  return <div className="min-h-screen bg-[#0D1B2A] text-[#E7E5DE] font-['Inter'] selection:bg-[#1D9E75]/30 overflow-x-hidden" data-id="element-63">
      {/* 1. HERO SECTION */}
      <section className="relative pt-28 pb-20 md:pt-40 md:pb-32 px-6" data-id="element-64">
        <AmbientField data-id="element-65" />

        <div className="max-w-5xl mx-auto text-center relative z-10" data-id="element-66">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} data-id="element-67">
            <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1D9E75]/10 text-[#3FCB9D] text-sm font-semibold mb-8 border border-[#1D9E75]/25" data-id="element-68">
              <span className="relative flex h-2 w-2" data-id="element-69">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#1D9E75] opacity-75" data-id="element-70" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#1D9E75]" data-id="element-71" />
              </span>
              Instant Download · $17 One-Time Payment
            </motion.div>

            <h1 className="text-5xl md:text-7xl lg:text-8xl font-extrabold text-[#FAF9F6] tracking-tight mb-8 leading-[0.98]" data-id="element-72">
              Stop looking like a beginner.
              <br className="hidden md:block" data-id="element-73" />
              <span className="relative inline-block text-transparent bg-clip-text bg-gradient-to-r from-[#1D9E75] via-[#3FCB9D] to-[#C9A84C]" data-id="element-74">
                Start onboarding like a pro.
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-[#9FA8B3] mb-12 max-w-3xl mx-auto leading-relaxed" data-id="element-75">
              The done-for-you system built for Virtual Assistants and
              freelancers in South Africa, the Philippines, and beyond —
              protect against scope creep, look incredibly professional, and
              get paid on time.
            </p>

            <div className="flex flex-col items-center justify-center gap-4 mb-20" data-id="element-76">
              <Button className="w-full sm:w-auto group animate-pulse-cta" data-id="element-77">
                Get Instant Access — $17
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-78" />
              </Button>
              <p className="text-sm font-medium text-[#9FA8B3] flex items-center gap-2" data-id="element-79">
                <span className="inline-flex -space-x-2" data-id="element-80">
                  <span className="w-6 h-6 rounded-full bg-[#1D9E75]/40 border border-[#1D9E75]/50" data-id="element-81" />
                  <span className="w-6 h-6 rounded-full bg-[#C9A84C]/40 border border-[#C9A84C]/50" data-id="element-82" />
                  <span className="w-6 h-6 rounded-full bg-[#1D9E75]/30 border border-[#1D9E75]/40" data-id="element-83" />
                </span>
                Join 200+ VAs who already onboard clients like pros
              </p>
            </div>
          </motion.div>

          {/* Hero mockup — fanned document stack, brand-true */}
          <motion.div initial={{
          opacity: 0,
          y: 50
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.3
        }} className="relative mx-auto max-w-4xl" data-id="element-84">
            <div className="relative h-[380px] md:h-[420px] flex items-center justify-center" data-id="element-85">
              <div className="absolute bottom-6 w-3/4 h-12 bg-[#1D9E75]/20 blur-3xl rounded-full" data-id="element-86" />

              {[{
              kind: 'emails' as const,
              x: -210,
              rotate: -14,
              z: 1,
              delay: 0.5
            }, {
              kind: 'intake' as const,
              x: -100,
              rotate: -6,
              z: 2,
              delay: 0.45
            }, {
              kind: 'welcome' as const,
              x: 0,
              rotate: 0,
              z: 5,
              delay: 0.35
            }, {
              kind: 'pricing' as const,
              x: 100,
              rotate: 6,
              z: 2,
              delay: 0.45
            }, {
              kind: 'contract' as const,
              x: 210,
              rotate: 14,
              z: 1,
              delay: 0.5
            }].map(doc => <motion.div key={doc.kind} initial={{
              opacity: 0,
              y: 60,
              rotate: 0
            }} animate={{
              opacity: 1,
              y: 0,
              rotate: doc.rotate
            }} whileHover={{
              y: -16,
              scale: 1.06,
              zIndex: 20,
              transition: {
                duration: 0.25
              }
            }} transition={{
              duration: 0.7,
              delay: doc.delay,
              ease: 'easeOut'
            }} className="absolute drop-shadow-2xl cursor-pointer" style={{
              left: '50%',
              marginLeft: doc.x - 70,
              zIndex: doc.z,
              width: 140
            }} data-id="element-87">
                  <DocMockup kind={doc.kind} className="w-[140px] h-[187px] rounded-xl shadow-2xl" data-id="element-88" />
                </motion.div>)}
            </div>
            <p className="text-[#6B7480] font-medium tracking-widest text-xs uppercase mt-4" data-id="element-89">
              The VA Pro-Onboarding OS — 5 documents, instantly editable
            </p>
          </motion.div>
        </div>
      </section>

      {/* 2. PAIN SECTION */}
      <section className="py-24 bg-[#0A1622] border-y border-white/5 px-6 relative" data-id="element-90">
        <div className="max-w-6xl mx-auto" data-id="element-91">
          <FadeIn className="text-center mb-16" data-id="element-92">
            <h2 className="text-3xl md:text-4xl font-bold text-[#FAF9F6] mb-4" data-id="element-93">
              Sound familiar?
            </h2>
            <p className="text-[#9FA8B3] text-lg" data-id="element-94">
              The hidden costs of winging your client onboarding.
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-95">
            {[{
            icon: <Frown className="w-8 h-8 text-rose-400" data-id="element-96" />,
            title: 'Messy client onboarding that makes you look amateur',
            desc: 'Sending 5 different emails with Google Doc links and confusing instructions makes clients doubt they hired an expert.'
          }, {
            icon: <ShieldAlert className="w-8 h-8 text-[#C9A84C]" data-id="element-97" />,
            title: 'No contract = no protection from scope creep',
            desc: "Clients asking for 'just one more quick thing' because you never set clear boundaries in writing from day one."
          }, {
            icon: <Clock className="w-8 h-8 text-blue-400" data-id="element-98" />,
            title: 'Chasing late payments with no system',
            desc: "Feeling awkward asking for money because you didn't establish a professional invoicing and payment policy upfront."
          }].map((item, i) => <FadeIn key={i} delay={i * 0.12} className="group bg-[#0D1B2A] border border-white/10 p-8 rounded-2xl hover:border-[#1D9E75]/40 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(29,158,117,0.3)]" data-id="element-99">
                <div className="w-14 h-14 rounded-xl bg-white/5 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" data-id="element-100">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-[#FAF9F6] mb-4 leading-snug" data-id="element-101">
                  {item.title}
                </h3>
                <p className="text-[#9FA8B3] leading-relaxed" data-id="element-102">{item.desc}</p>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* 3. SOLUTION SECTION */}
      <section className="py-24 px-6 relative overflow-hidden" data-id="element-103">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] bg-[#C9A84C]/[0.04] blur-[120px] rounded-full pointer-events-none" data-id="element-104" />
        <div className="max-w-4xl mx-auto relative z-10" data-id="element-105">
          <FadeIn className="text-center mb-16" data-id="element-106">
            <h2 className="text-4xl md:text-5xl font-bold text-[#FAF9F6] mb-6" data-id="element-107">
              Introducing{' '}
              <span className="text-[#1D9E75]" data-id="element-108">The VA Pro-Onboarding OS</span>
            </h2>
            <p className="text-xl text-[#9FA8B3]" data-id="element-109">
              Everything you need to look like a premium service provider,
              instantly.
            </p>
          </FadeIn>

          <div className="space-y-4" data-id="element-110">
            {[{
            icon: <FileText data-id="element-111" />,
            title: 'VA Service Contract Template',
            desc: 'Plug-and-play legal protection to stop scope creep dead in its tracks.'
          }, {
            icon: <LayoutTemplate data-id="element-112" />,
            title: 'Client Welcome Packet (Canva)',
            desc: 'A stunning guide that sets expectations, boundaries, and next steps.'
          }, {
            icon: <CheckCircle2 data-id="element-113" />,
            title: 'Client Intake Form (Google Forms)',
            desc: 'Gather all passwords, assets, and info before you start working.'
          }, {
            icon: <Mail data-id="element-114" />,
            title: 'Email Scripts Pack',
            desc: 'Copy-paste templates for pitching, onboarding, and following up.'
          }, {
            icon: <CreditCard data-id="element-115" />,
            title: 'Service & Pricing Guide (Canva)',
            desc: 'Present your packages beautifully to justify premium rates.'
          }].map((item, i) => <FadeIn key={i} delay={i * 0.08} data-id="element-116">
                <motion.div whileHover={{
              x: 6
            }} transition={{
              duration: 0.2
            }} className="flex items-start sm:items-center gap-4 p-6 rounded-xl bg-white/[0.03] border border-white/10 hover:bg-white/[0.06] hover:border-[#1D9E75]/30 transition-colors" data-id="element-117">
                  <div className="p-3 bg-[#1D9E75]/10 text-[#3FCB9D] rounded-lg shrink-0" data-id="element-118">
                    {item.icon}
                  </div>
                  <div data-id="element-119">
                    <h3 className="text-lg font-bold text-[#FAF9F6]" data-id="element-120">{item.title}</h3>
                    <p className="text-[#9FA8B3] mt-1" data-id="element-121">{item.desc}</p>
                  </div>
                </motion.div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* 4. PROOF SECTION */}
      <section className="py-24 bg-[#0A1622] border-y border-white/5 px-6" data-id="element-122">
        <div className="max-w-6xl mx-auto" data-id="element-123">
          <FadeIn className="text-center mb-16" data-id="element-124">
            <h2 className="text-3xl md:text-4xl font-bold text-[#FAF9F6]" data-id="element-125">
              Don't just take our word for it
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-126">
            {[{
            quote: 'I used to send messy emails to onboard clients. This OS made me look like an agency. I literally doubled my rates the next week because I felt so much more confident.',
            author: 'Sarah M.',
            role: 'Virtual Assistant, South Africa',
            initials: 'SM'
          }, {
            quote: "The contract template alone is worth 10x the price. I finally have boundaries and clients actually respect my working hours now. Best $17 I've spent on my business.",
            author: 'Priya K.',
            role: 'Freelance SMM, India',
            initials: 'PK'
          }, {
            quote: 'I was so scared of looking unprofessional to international clients. The Canva templates are beautiful and so easy to edit. I got my first US client using this exact system!',
            author: 'Maria L.',
            role: 'Admin Assistant, Philippines',
            initials: 'ML'
          }].map((t, i) => <FadeIn key={i} delay={i * 0.1} className="bg-[#0D1B2A] border border-white/10 p-8 rounded-2xl relative hover:border-[#C9A84C]/30 transition-colors duration-300" data-id="element-127">
                <div className="flex gap-1 mb-6 text-[#C9A84C]" data-id="element-128">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-5 h-5 fill-current" data-id="element-129" />)}
                </div>
                <p className="text-[#D7DAE0] text-lg mb-8 italic" data-id="element-130">"{t.quote}"</p>
                <div className="flex items-center gap-3" data-id="element-131">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1D9E75] to-[#0D1B2A] border border-[#C9A84C]/40 flex items-center justify-center text-xs font-bold text-[#FAF9F6]" data-id="element-132">
                    {t.initials}
                  </div>
                  <div data-id="element-133">
                    <div className="font-bold text-[#FAF9F6]" data-id="element-134">{t.author}</div>
                    <div className="text-[#6B7480] text-sm" data-id="element-135">{t.role}</div>
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* 5. WHAT'S INCLUDED DEEP DIVE */}
      <section className="py-24 px-6" data-id="element-136">
        <div className="max-w-5xl mx-auto" data-id="element-137">
          <FadeIn className="text-center mb-16" data-id="element-138">
            <h2 className="text-3xl md:text-4xl font-bold text-[#FAF9F6] mb-4" data-id="element-139">
              Exactly what you get inside
            </h2>
            <p className="text-[#9FA8B3]" data-id="element-140">
              Instant access to all 5 templates the moment you purchase.
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-141">
            {[{
            kind: 'contract' as const,
            title: '1. VA Service Contract',
            items: ['Fill-in-the-blank Google Doc format', 'Payment terms & late fee clauses', 'Scope creep protection', 'Termination clauses']
          }, {
            kind: 'welcome' as const,
            title: '2. Client Welcome Packet',
            items: ['Fully editable Canva template', 'Communication boundaries page', 'Project timeline layout', 'What to expect next section']
          }, {
            kind: 'intake' as const,
            title: '3. Client Intake Form',
            items: ['1-click copy to your Google Drive', '20+ strategic questions', 'Password & asset collection', 'Goal setting section']
          }, {
            kind: 'emails' as const,
            title: '4. Email Scripts Pack',
            items: ["The 'Welcome Aboard' email", "The 'Invoice Attached' email", "The 'Out of Scope' pushback email", "The 'Following Up' email"]
          }, {
            kind: 'pricing' as const,
            title: '5. Service & Pricing Guide',
            items: ['Beautiful Canva template', 'Package tier layouts', 'A la carte services page', 'FAQ section for clients']
          }].map((module, i) => <FadeIn key={i} delay={i * 0.08} className="group bg-white/[0.03] border border-white/10 p-7 rounded-2xl flex gap-6 hover:border-[#1D9E75]/30 hover:bg-white/[0.05] transition-all duration-300" data-id="element-142">
                <DocMockup kind={module.kind} className="w-[72px] h-[96px] shrink-0 rounded-md shadow-lg group-hover:-translate-y-1 group-hover:rotate-[-2deg] transition-transform duration-300" data-id="element-143" />
                <div data-id="element-144">
                  <h3 className="text-lg font-bold text-[#3FCB9D] mb-4" data-id="element-145">{module.title}</h3>
                  <ul className="space-y-2.5" data-id="element-146">
                    {module.items.map((item, j) => <li key={j} className="flex items-start gap-2.5 text-[#C7CCD3] text-sm" data-id="element-147">
                        <CheckCircle2 className="w-4 h-4 text-[#1D9E75] shrink-0 mt-0.5" data-id="element-148" />
                        <span data-id="element-149">{item}</span>
                      </li>)}
                  </ul>
                </div>
              </FadeIn>)}

            <FadeIn delay={0.5} className="relative bg-gradient-to-br from-[#1D9E75]/15 to-[#C9A84C]/10 border border-[#1D9E75]/30 p-8 rounded-2xl flex flex-col justify-center items-center text-center overflow-hidden" data-id="element-150">
              <Sparkles className="absolute top-4 right-4 w-5 h-5 text-[#C9A84C]/50" data-id="element-151" />
              <h3 className="text-2xl font-bold text-[#FAF9F6] mb-2" data-id="element-152">
                Total Value: $37
              </h3>
              <p className="text-[#3FCB9D] font-medium mb-6" data-id="element-153">
                Yours today for just $17
              </p>
              <Button className="w-full" data-id="element-154">Get It Now</Button>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* 6. PRICING BOX */}
      <section className="py-24 bg-[#0A1622] px-6 relative overflow-hidden" data-id="element-155">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-[#1D9E75]/[0.06] blur-[110px] rounded-full pointer-events-none" data-id="element-156" />

        <div className="max-w-lg mx-auto relative z-10" data-id="element-157">
          <FadeIn className="relative rounded-2xl overflow-hidden mb-8 shadow-2xl shadow-black/40 border border-white/10" data-id="element-158">
            <img src={HERO_STACK_IMAGE} alt="The VA Pro-Onboarding OS — full document bundle" loading="lazy" className="w-full h-auto object-cover" data-id="element-159" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A1622] via-transparent to-transparent" data-id="element-160" />
          </FadeIn>

          <FadeIn delay={0.1} className="relative bg-[#0D1B2A] border-2 border-[#C9A84C]/30 rounded-3xl p-8 md:p-12 shadow-2xl shadow-black/40" data-id="element-161">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#C9A84C] text-[#0D1B2A] text-xs font-bold rounded-full tracking-wide shadow-lg whitespace-nowrap" data-id="element-162">
              LAUNCH PRICE — LIMITED TIME
            </div>

            <div className="text-center mb-8 mt-2" data-id="element-163">
              <h2 className="text-3xl font-bold text-[#FAF9F6] mb-2" data-id="element-164">
                The VA Pro-Onboarding OS
              </h2>
              <div className="flex items-center justify-center gap-3 mb-4" data-id="element-165">
                <span className="text-[#6B7480] line-through text-xl" data-id="element-166">$37</span>
                <span className="text-5xl font-extrabold text-[#FAF9F6]" data-id="element-167">$17</span>
              </div>
              <p className="text-[#3FCB9D] font-medium" data-id="element-168">
                One-time payment. No subscriptions. Instant download.
              </p>
            </div>

            <div className="space-y-3.5 mb-8" data-id="element-169">
              {['VA Service Contract Template', 'Client Welcome Packet (Canva)', 'Client Intake Form (Google Forms)', 'Email Scripts Pack', 'Service & Pricing Guide (Canva)', 'Lifetime access to updates'].map((item, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -10
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.06
            }} className="flex items-center gap-3" data-id="element-170">
                  <CheckCircle2 className="w-5 h-5 text-[#1D9E75] shrink-0" data-id="element-171" />
                  <span className="text-[#D7DAE0] font-medium" data-id="element-172">{item}</span>
                </motion.div>)}
            </div>

            <Button className="w-full py-5 text-xl" data-id="element-173">Get Instant Access Now</Button>

            <p className="text-center text-[#6B7480] text-sm mt-6 flex items-center justify-center gap-2" data-id="element-174">
              <Lock className="w-4 h-4" data-id="element-175" /> Secure, encrypted checkout
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 7. FAQ */}
      <section className="py-24 px-6" data-id="element-176">
        <div className="max-w-3xl mx-auto" data-id="element-177">
          <FadeIn className="text-center mb-16" data-id="element-178">
            <h2 className="text-3xl md:text-4xl font-bold text-[#FAF9F6]" data-id="element-179">
              Frequently Asked Questions
            </h2>
          </FadeIn>

          <div className="space-y-4" data-id="element-180">
            {[{
            q: 'What format are the files?',
            a: "You'll receive a PDF with instant access links. The templates are provided in Canva (Welcome Packet, Pricing Guide), Google Docs (Contract, Email Scripts), and Google Forms (Intake Form)."
          }, {
            q: 'Do I need special software or paid accounts?',
            a: 'No! All templates can be used with 100% free accounts on Canva and Google Workspace (Gmail/Google Drive).'
          }, {
            q: 'Can I customize the templates to match my brand?',
            a: 'Absolutely. The Canva templates are fully customizable — you can change colors, fonts, images, and text to match your exact branding in minutes.'
          }, {
            q: 'Is this for beginners or experienced freelancers?',
            a: "Both! If you're a beginner, this gives you a professional foundation from day one. If you're experienced but disorganized, this will streamline your messy process and help you justify higher rates."
          }, {
            q: "What if I'm not satisfied?",
            a: 'Because this is a digital product with instant access to all files, we generally do not offer refunds. However, if you have any issues accessing or using the files, our support team is here to help you succeed.'
          }].map((faq, i) => <FAQItem key={i} question={faq.q} answer={faq.a} data-id="element-181" />)}
          </div>
        </div>
      </section>

      {/* 8. FINAL CTA */}
      <section className="py-32 px-6 bg-gradient-to-b from-[#0D1B2A] to-[#0A1622] border-t border-white/5 text-center relative overflow-hidden" data-id="element-182">
        <AmbientField data-id="element-183" />
        <div className="max-w-3xl mx-auto relative z-10" data-id="element-184">
          <FadeIn data-id="element-185">
            <div className="inline-flex items-center gap-2 mb-6 text-[#C9A84C]" data-id="element-186">
              <Zap className="w-5 h-5" data-id="element-187" />
              <span className="text-sm font-semibold uppercase tracking-widest" data-id="element-188">Last call</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-extrabold text-[#FAF9F6] mb-8 tracking-tight" data-id="element-189">
              Stop looking like a beginner.
              <br data-id="element-190" />
              <span className="text-[#1D9E75]" data-id="element-191">Start onboarding like a pro.</span>
            </h2>
            <p className="text-xl text-[#9FA8B3] mb-10" data-id="element-192">
              Join hundreds of freelancers who have upgraded their client
              experience and protected their time.
            </p>
            <Button className="px-10 py-5 text-xl" data-id="element-193">Get The OS For $17</Button>
          </FadeIn>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 text-center text-[#5A6370] text-sm border-t border-white/5 bg-[#0A1622]" data-id="element-194">
        <p data-id="element-195">
          © {new Date().getFullYear()} The VA Pro-Onboarding OS · Digital Solvex. All rights reserved.
        </p>
      </footer>
    </div>;
}
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border border-white/10 rounded-xl bg-white/[0.03] overflow-hidden hover:border-[#1D9E75]/20 transition-colors" data-id="element-196">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-197">
        <span className="font-semibold text-lg text-[#E7E5DE]" data-id="element-198">{question}</span>
        <ChevronDown className={`w-5 h-5 text-[#6B7480] transition-transform duration-300 ${isOpen ? 'rotate-180 text-[#1D9E75]' : ''}`} data-id="element-199" />
      </button>
      <AnimatePresence data-id="element-200">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-201">
            <div className="px-6 pb-5 text-[#9FA8B3] leading-relaxed" data-id="element-202">{answer}</div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}